import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { errorMessage, fieldErrors } from '../../lib/api';
import { Alert, Button, Field, TextArea } from '../ui';
import { TimeField } from '../TimeField';

export interface DetailsValue {
  title: string;
  date?: string;
  startTime?: string;
  durationMinutes: number;
  location: string | null;
  focus: string | null;
  whatToBring: string | null;
  notes: string | null;
  playersExpected: number | null;
  courtsAvailable: number | null;
}

/** Edits a session's (or template's) details. Plans are saved separately by the builder. */
export function DetailsForm({ value, onSave, onDone, isTemplate }: { value: DetailsValue; onSave: (v: Record<string, unknown>) => Promise<unknown>; onDone: () => void; isTemplate?: boolean }) {
  const [v, setV] = useState({
    title: value.title,
    date: value.date ?? '',
    startTime: value.startTime ?? '',
    durationMinutes: String(value.durationMinutes),
    location: value.location ?? '',
    focus: value.focus ?? '',
    whatToBring: value.whatToBring ?? '',
    notes: value.notes ?? '',
    playersExpected: value.playersExpected ? String(value.playersExpected) : '',
    courtsAvailable: value.courtsAvailable ? String(value.courtsAvailable) : '',
  });
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () =>
      onSave({
        title: v.title,
        ...(isTemplate ? {} : { date: v.date, startTime: v.startTime }),
        durationMinutes: v.durationMinutes,
        location: v.location,
        focus: v.focus,
        whatToBring: v.whatToBring,
        notes: v.notes,
        playersExpected: v.playersExpected || null,
        courtsAvailable: v.courtsAvailable || null,
      }),
    onSuccess: onDone,
  });
  const f = fieldErrors(save.error);

  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
      {save.isError && !Object.keys(f).length && <Alert>{errorMessage(save.error)}</Alert>}
      <Field label="Name" value={v.title} onChange={set('title')} error={f.title} maxLength={120} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {!isTemplate && (
          <>
            <Field label="Date" type="date" value={v.date} onChange={set('date')} error={f.date} className="field-input col-span-2 sm:col-span-1" />
            <TimeField label="Starts" value={v.startTime} onChange={(t) => set('startTime')({ target: { value: t } })} error={f.startTime} />
          </>
        )}
        <Field label="Length (min)" type="number" min={10} max={360} step={5} value={v.durationMinutes} onChange={set('durationMinutes')} error={f.durationMinutes} className="field-input tnum" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Location" value={v.location} onChange={set('location')} error={f.location} placeholder="e.g. Stadium, court 2" maxLength={120} />
        <Field label="Focus" value={v.focus} onChange={set('focus')} error={f.focus} placeholder="e.g. Help defence and closeouts" maxLength={200} />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:max-w-[360px]">
        <Field label="Players expected" type="number" min={1} max={60} value={v.playersExpected} onChange={set('playersExpected')} error={f.playersExpected} className="field-input tnum" />
        <Field label="Courts available" type="number" min={1} max={6} value={v.courtsAvailable} onChange={set('courtsAvailable')} error={f.courtsAvailable} className="field-input tnum" />
      </div>
      <TextArea label="What to bring" value={v.whatToBring} onChange={set('whatToBring')} error={f.whatToBring} maxLength={500} hint="Players and parents see this." />
      <TextArea label="Coach notes" value={v.notes} onChange={set('notes')} error={f.notes} maxLength={4000} hint="Only coaches and read-only members see these." />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" loading={save.isPending}>
          Save details
        </Button>
      </div>
    </form>
  );
}
